import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@/shared/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/ui/card';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/shared/ui/form';
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from '@/shared/ui/select';
import { MonthPicker } from '@/shared/widgets/MonthPicker';
import { useToast } from '@/shared/hooks/use-toast';
import {
  useCreateReceivable,
  useUpdateReceivable,
  useReceivable,
  useCreateReceivableDocument,
  useCreateReceivableTax,
  useCreateReceivableTaxDocument,
} from '../hooks';
import { useReceivableShareConfigs } from '@/features/receivable-share-configs/hooks';
import {
  ReceivableCurrency,
  ReceivableStatus,
  ReceivableStore,
  PendingReceivableDocument,
  PendingReceivableTax,
  type CreateReceivablePayload,
} from '../utils/types';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { CurrencyInput } from '@/shared/ui/currency-input';
import { ReceivableDocumentsSection, ReceivableTaxesSection, ReceivableStatusBadge } from '../components';
import { Alert, AlertDescription } from '@/shared/ui/alert';
import { receivableFormSchema, type ReceivableFormValues } from '../model/receivable.schema';
import { FormFooter } from '@/shared/widgets';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/ui/alert-dialog';

// Formata mês/ano para "YYYY-MM" (formato aceito pela API para relatedDate e paidDate)
const formatYearMonth = (year?: number, month?: number): string | undefined => {
  if (!year || !month) return undefined;
  return `${year}-${String(month).padStart(2, '0')}`;
};

type FormData = ReceivableFormValues;

export function ReceivableForm() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const { toast } = useToast();

  const { receivable, isLoading: loadingReceivable } = useReceivable(id, { enabled: isEdit });
  const { createReceivable, isLoading: creating } = useCreateReceivable();
  const { updateReceivable, isLoading: updating } = useUpdateReceivable();
  const { createDocument: uploadReceivableDocument } = useCreateReceivableDocument();
  const { createTax: uploadReceivableTax } = useCreateReceivableTax();
  const { createTaxDocument: uploadReceivableTaxDocument } = useCreateReceivableTaxDocument();
  const [pendingDocuments, setPendingDocuments] = useState<PendingReceivableDocument[]>([]);
  const [uploadingPendingDocs, setUploadingPendingDocs] = useState(false);
  const [pendingTaxes, setPendingTaxes] = useState<PendingReceivableTax[]>([]);
  const [uploadingPendingTaxes, setUploadingPendingTaxes] = useState(false);
  const [showConfirmConcludedDialog, setShowConfirmConcludedDialog] = useState(false);

  const { shareConfigs, isLoading: loadingShareConfigs } = useReceivableShareConfigs({ page: 1, limit: 100 });
  const shareConfigOptions = useMemo(
    () => shareConfigs.map((config) => ({ value: config.id, label: config.name })),
    [shareConfigs],
  );

  const form = useForm<FormData>({
    resolver: zodResolver(receivableFormSchema),
    defaultValues: {
      currency: ReceivableCurrency.BRL,
      status: ReceivableStatus.WAITING,
      store: ReceivableStore.APPLE,
      grossValue: 0,
      relatedMonth: undefined,
      relatedYear: undefined,
      paidMonth: undefined,
      paidYear: undefined,
      receivableShareConfigId: undefined,
    },
  });
  const currentStatus = form.watch('status');
  const watchedRelatedMonth = form.watch('relatedMonth');
  const watchedRelatedYear = form.watch('relatedYear');
  const watchedPaidMonth = form.watch('paidMonth');
  const watchedPaidYear = form.watch('paidYear');
  const isStatusConcluded = currentStatus === ReceivableStatus.CONCLUDED;
  const canMarkAsConcluded = isEdit && currentStatus === ReceivableStatus.WAITING;

  // Campos principais ficam desabilitados no modo edição
  const isFieldLocked = isEdit;

  // Calcula o mês/ano mínimo para paidDate com base em relatedMonth/relatedYear
  const paidMinMonthYear = useMemo(() => {
    if (watchedRelatedMonth && watchedRelatedYear) {
      return { month: watchedRelatedMonth, year: watchedRelatedYear };
    }
    return null;
  }, [watchedRelatedMonth, watchedRelatedYear]);

  const handleMarkAsConcluded = () => {
    if (!id || !canMarkAsConcluded) return;
    setShowConfirmConcludedDialog(true);
  };

  const handleConfirmMarkAsConcluded = async () => {
    if (!id || !canMarkAsConcluded) return;

    setShowConfirmConcludedDialog(false);

    try {
      await updateReceivable(id, { status: ReceivableStatus.CONCLUDED });
      form.setValue('status', ReceivableStatus.CONCLUDED);
      toast({
        title: 'Recebível marcado como concluído',
        description: 'O cálculo será processado em breve.',
      });
      navigate('/receivables');
    } catch (error) {
      toast({
        title: 'Erro ao marcar como concluído',
        description: error instanceof Error ? error.message : 'Ocorreu um erro inesperado.',
        variant: 'destructive',
      });
    }
  };

  // Extrai month/year de relatedDate (legado) quando os campos novos não vêm da API
  const resolveRelatedMonthYear = (rec: NonNullable<typeof receivable>) => {
    if (rec.relatedMonth && rec.relatedYear) {
      return { month: rec.relatedMonth, year: rec.relatedYear };
    }
    // Fallback: parsear de relatedDate (YYYY-MM-DD ou ISO)
    if (rec.relatedDate) {
      const datePart = rec.relatedDate.includes('T')
        ? rec.relatedDate.split('T')[0]
        : rec.relatedDate;
      const [year, month] = datePart.split('-').map(Number);
      if (year && month) return { month, year };
    }
    return { month: undefined, year: undefined };
  };

  // Extrai month/year de paidDate do backend
  const resolvePaidMonthYear = (paidDate?: string) => {
    if (!paidDate) return { month: undefined, year: undefined };
    
    try {
      const datePart = paidDate.includes('T')
        ? paidDate.split('T')[0]
        : paidDate;
      const [year, month] = datePart.split('-').map(Number);
      if (year && month) return { month, year };
    } catch {
      return { month: undefined, year: undefined };
    }
    
    return { month: undefined, year: undefined };
  };

  // Aguarda tanto o receivable quanto a lista de shareConfigs carregarem
  // antes de resetar o form. Sem isso o Select de rateio fica vazio
  // porque o valor é setado antes das opções estarem disponíveis.
  useEffect(() => {
    if (receivable && !loadingShareConfigs) {
      const resolvedShareConfigId =
        receivable.receivableShareConfig?.id ?? receivable.receivableShareConfigId;
      const { month, year } = resolveRelatedMonthYear(receivable);
      const paidMonthYear = resolvePaidMonthYear(receivable.paidDate);
      
      form.reset({
        currency: receivable.currency,
        status: receivable.status,
        store: receivable.store,
        grossValue: receivable.grossValue,
        relatedMonth: month,
        relatedYear: year,
        paidMonth: paidMonthYear.month,
        paidYear: paidMonthYear.year,
        receivableShareConfigId: resolvedShareConfigId ?? undefined,
      });
    }
  }, [receivable, form, loadingShareConfigs]);

  const handlePendingDocumentCreate = (document: PendingReceivableDocument) => {
    setPendingDocuments((prev) => [...prev, document]);
  };

  const handlePendingDocumentUpdate = (document: PendingReceivableDocument) => {
    setPendingDocuments((prev) => prev.map((item) => (item.id === document.id ? document : item)));
  };

  const handlePendingDocumentDelete = (id: string) => {
    setPendingDocuments((prev) => prev.filter((item) => item.id !== id));
  };

  const handlePendingTaxCreate = (tax: PendingReceivableTax) => {
    setPendingTaxes((prev) => [...prev, tax]);
  };

  const handlePendingTaxUpdate = (tax: PendingReceivableTax) => {
    setPendingTaxes((prev) => prev.map((item) => (item.id === tax.id ? tax : item)));
  };

  const handlePendingTaxDelete = (id: string) => {
    setPendingTaxes((prev) => prev.filter((item) => item.id !== id));
  };

  const isSubmitting = creating || updating || uploadingPendingDocs || uploadingPendingTaxes;

  const onSubmit = async (data: FormData, event?: React.BaseSyntheticEvent) => {
    // Previne submissões automáticas não intencionais
    const submitter = event?.nativeEvent?.submitter as HTMLButtonElement | undefined;
    
    // Bloqueia se não houver submitter
    if (!submitter) {
      console.warn('[ReceivableForm] Submit bloqueado: submissão sem botão submitter');
      return;
    }

    // Bloqueia se o submitter não for um botão de submit do formulário
    // (previne que botões internos de componentes como MonthPicker disparem o submit)
    const submitterType = submitter.getAttribute('type');
    if (submitterType === 'button') {
      console.warn('[ReceivableForm] Submit bloqueado: botão com type="button" não pode submeter', {
        submitterText: submitter.textContent
      });
      return;
    }

    if (isEdit && id) {
      // No modo edição, só permite alterar status (campos principais bloqueados)
      // Documentos e taxas são gerenciados por seus próprios componentes
      await updateReceivable(id, { status: data.status });
      navigate('/receivables');
      return;
    }

    // Monta relatedDate como "YYYY-MM" para a API
    const relatedDate = formatYearMonth(data.relatedYear, data.relatedMonth);
    if (!relatedDate) {
      toast({
        title: 'Data de referência inválida',
        description: 'Selecione o mês e ano de referência.',
        variant: 'destructive',
      });
      return;
    }

    // Monta paidDate como "YYYY-MM" para a API (se preenchido)
    const paidDateFormatted = formatYearMonth(data.paidYear, data.paidMonth);

    const payload: CreateReceivablePayload = {
      currency: data.currency,
      status: data.status,
      store: data.store,
      grossValue: data.grossValue,
      relatedDate,
      receivableShareConfigId: data.receivableShareConfigId,
    };

    if (paidDateFormatted) payload.paidDate = paidDateFormatted;

    try {
      const created = await createReceivable(payload);
      if (!created?.id) {
        toast({
          title: 'Erro ao criar recebível',
          description: 'ID não retornado pela API. Tente novamente.',
          variant: 'destructive',
        });
        return;
      }

      if (pendingDocuments.length > 0) {
        setUploadingPendingDocs(true);
        let uploadedCount = 0;
        try {
          for (const document of pendingDocuments) {
            await uploadReceivableDocument({
              receivableId: created.id,
              documentType: document.documentType,
              description: document.description,
              documentUrl: document.documentUrl,
              file: document.file,
            });
            uploadedCount += 1;
          }
          setPendingDocuments([]);
        } catch (error) {
          if (uploadedCount > 0) {
            setPendingDocuments((current) => current.slice(uploadedCount));
          }
          toast({
            title: 'Alguns documentos não foram enviados',
            description: 'Você poderá anexá-los novamente na edição do recebível.',
            variant: 'destructive',
          });
        } finally {
          setUploadingPendingDocs(false);
        }
      }

      if (pendingTaxes.length > 0) {
        setUploadingPendingTaxes(true);
        let uploadedTaxes = 0;
        try {
          for (const tax of pendingTaxes) {
            const createdTax = await uploadReceivableTax({
              receivableId: created.id,
              currency: tax.currency,
              type: tax.type,
              description: tax.description,
              document: tax.document,
              value: tax.value,
            });
            if (tax.documents && tax.documents.length > 0) {
              for (const document of tax.documents) {
                try {
                  await uploadReceivableTaxDocument({
                    receivableTaxId: createdTax.id,
                    documentType: document.documentType,
                    description: document.description,
                    documentUrl: document.documentUrl,
                    file: document.file,
                  });
                } catch (error) {
                  console.error('Erro ao anexar documento da taxa:', error);
                }
              }
            }
            uploadedTaxes += 1;
          }
          setPendingTaxes([]);
        } catch (error) {
          if (uploadedTaxes > 0) {
            setPendingTaxes((current) => current.slice(uploadedTaxes));
          }
          toast({
            title: 'Algumas taxas não foram enviadas',
            description: 'Você poderá cadastrá-las novamente na edição do recebível.',
            variant: 'destructive',
          });
        } finally {
          setUploadingPendingTaxes(false);
        }
      }

      navigate('/receivables');
    } catch (error) {
      console.error('Erro ao salvar recebível:', error);
      toast({
        title: 'Erro ao salvar',
        description: error instanceof Error ? error.message : 'Ocorreu um erro inesperado. Tente novamente.',
        variant: 'destructive',
      });
    }
  };

  if (isEdit && loadingReceivable) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/receivables')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Carregando...</h1>
            <p className="text-muted-foreground">Buscando dados do recebível</p>
          </div>
        </div>
        <Card>
          <CardContent className="p-6">
            <div className="flex justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 ">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/receivables')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            {isEdit ? 'Editar Recebível' : 'Novo Recebível'}
          </h1>
          <p className="text-muted-foreground">
            {isEdit
              ? 'Gerencie documentos e taxas do recebível'
              : 'Preencha os dados para criar um novo recebível'}
          </p>
        </div>
      </div>

      <Card className='bg-white/[0.03] border-white/[0.06]'>
        <CardHeader>
          <CardTitle>Informações do Recebível</CardTitle>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              {/* Status do Recebível */}
              <div className="space-y-4 rounded-lg border border-border bg-card p-6">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <h3 className="text-base font-semibold text-foreground">Status do Recebível</h3>
                      <ReceivableStatusBadge status={currentStatus} />
                    </div>
                    {currentStatus === ReceivableStatus.WAITING && (
                      <div className="space-y-3">
                        <p className="text-sm text-muted-foreground">
                          O recebível está aguardando processamento. Você pode editar as informações e anexar documentos.
                        </p>
                        <Alert className="border-amber-500/30 bg-amber-500/5">
                          <AlertDescription className="text-sm font-medium text-foreground">
                            <strong className="text-amber-600 dark:text-amber-400">Importante:</strong> Ao marcar como concluído, o sistema iniciará o cálculo automático dos valores.
                          </AlertDescription>
                        </Alert>
                      </div>
                    )}
                    {currentStatus === ReceivableStatus.RECEIVED && (
                      <div className="space-y-2">
                        <p className="text-sm text-muted-foreground">
                          O recebível foi recebido e está pronto para processamento.
                        </p>
                      </div>
                    )}
                    {currentStatus === ReceivableStatus.CALCULATING && (
                      <div className="space-y-2">
                        <p className="text-sm text-muted-foreground">
                          O recebível está sendo processado. Os valores estão sendo calculados automaticamente.
                        </p>
                      </div>
                    )}
                    {currentStatus === ReceivableStatus.CALCULATED && (
                      <div className="space-y-2">
                        <p className="text-sm text-muted-foreground">
                          O recebível foi processado com sucesso. Todos os cálculos foram concluídos.
                        </p>
                      </div>
                    )}
                    {currentStatus === ReceivableStatus.CONCLUDED && (
                      <div className="space-y-2">
                        <p className="text-sm text-muted-foreground">
                          O recebível foi marcado como concluído e está aguardando processamento.
                        </p>
                      </div>
                    )}
                    {currentStatus === ReceivableStatus.FAIL && (
                      <div className="space-y-2">
                        <p className="text-sm text-muted-foreground">
                          Ocorreu um erro durante o processamento do recebível.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {isEdit && (
                <Alert className="border-blue-500/30 bg-blue-500/5">
                  <AlertDescription className="text-sm text-foreground">
                    Os campos principais do recebível não podem ser alterados após a criação. Você pode gerenciar documentos e taxas abaixo.
                  </AlertDescription>
                </Alert>
              )}

              <div className="grid gap-6 md:grid-cols-2">
                {/* Moeda */}
                <FormField
                  control={form.control}
                  name="currency"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Moeda</FormLabel>
                      <Select value={field.value} onValueChange={field.onChange} disabled={isFieldLocked}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Selecione a moeda" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value={ReceivableCurrency.BRL}>BRL</SelectItem>
                          <SelectItem value={ReceivableCurrency.USD}>USD</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormDescription>Moeda do recebível</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                {/* Loja */}
                <FormField
                  control={form.control}
                  name="store"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Loja</FormLabel>
                      <Select value={field.value} onValueChange={field.onChange} disabled={isFieldLocked}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Selecione a loja" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value={ReceivableStore.APPLE}>Apple</SelectItem>
                          <SelectItem value={ReceivableStore.GOOGLE}>Google</SelectItem>
                          <SelectItem value={ReceivableStore.STRIPE}>Stripe</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormDescription>Origem do recebível</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid gap-6 md:grid-cols-2">
                {/* Configuração de Rateio */}
                <FormField
                  control={form.control}
                  name="receivableShareConfigId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Configuração de rateio *</FormLabel>
                      <Select
                        value={field.value}
                        onValueChange={field.onChange}
                        disabled={isFieldLocked || loadingShareConfigs || isSubmitting}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Selecione a configuração" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {loadingShareConfigs ? (
                            <SelectItem value="__loading__" disabled>
                              Carregando configurações...
                            </SelectItem>
                          ) : (
                            shareConfigOptions.map((option) => (
                              <SelectItem key={option.value} value={option.value}>
                                {option.label}
                              </SelectItem>
                            ))
                          )}
                        </SelectContent>
                      </Select>
                      <FormDescription>
                        Define os pesos utilizados para o cálculo do recebível.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                {/* Valor Bruto */}
                <FormField
                  control={form.control}
                  name="grossValue"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Valor Bruto</FormLabel>
                      <FormControl>
                        <CurrencyInput
                          currency={form.watch('currency')}
                          value={field.value}
                          onValueChange={(num) => field.onChange(num ?? 0)}
                          placeholder="R$ 0,00"
                          disabled={isFieldLocked}
                        />
                      </FormControl>
                      <FormDescription>Valor bruto do recebível</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid gap-6 md:grid-cols-2">
                {/* Data de Referência (Mês/Ano) */}
                <FormField
                  control={form.control}
                  name="relatedMonth"
                  render={() => (
                    <FormItem className="flex flex-col w-full">
                      <FormLabel>Data de Referência</FormLabel>
                      <FormControl>
                        <MonthPicker
                          value={
                            watchedRelatedMonth && watchedRelatedYear
                              ? { month: watchedRelatedMonth, year: watchedRelatedYear }
                              : null
                          }
                          onChange={(val) => {
                            if (val) {
                              form.setValue('relatedMonth', val.month, { shouldValidate: false, shouldDirty: true });
                              form.setValue('relatedYear', val.year, { shouldValidate: false, shouldDirty: true });
                            } else {
                              form.setValue('relatedMonth', undefined as any, { shouldValidate: false, shouldDirty: true });
                              form.setValue('relatedYear', undefined as any, { shouldValidate: false, shouldDirty: true });
                            }
                          }}
                          placeholder="Selecione mês/ano de referência"
                          disabled={isFieldLocked}
                        />
                      </FormControl>
                      <FormDescription>Selecione o mês/ano base do recebível (dia sempre 1)</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                {/* Data de Recebimento */}
                <FormField
                  control={form.control}
                  name="paidMonth"
                  render={() => (
                    <FormItem className="flex flex-col w-full">
                      <FormLabel>Recebido em</FormLabel>
                      <FormControl>
                        <MonthPicker
                          value={
                            watchedPaidMonth && watchedPaidYear
                              ? { month: watchedPaidMonth, year: watchedPaidYear }
                              : null
                          }
                          onChange={(val) => {
                            if (val) {
                              // Valida se a data de recebimento não é anterior à data de referência
                              if (paidMinMonthYear) {
                                const selectedDate = new Date(val.year, val.month - 1, 1);
                                const minDate = new Date(paidMinMonthYear.year, paidMinMonthYear.month - 1, 1);
                                
                                if (selectedDate < minDate) {
                                  toast({
                                    title: 'Data inválida',
                                    description: 'A data de recebimento não pode ser anterior à data de referência.',
                                    variant: 'destructive',
                                  });
                                  return;
                                }
                              }
                              
                              form.setValue('paidMonth', val.month, { shouldValidate: false, shouldDirty: true });
                              form.setValue('paidYear', val.year, { shouldValidate: false, shouldDirty: true });
                            } else {
                              form.setValue('paidMonth', undefined as any, { shouldValidate: false, shouldDirty: true });
                              form.setValue('paidYear', undefined as any, { shouldValidate: false, shouldDirty: true });
                            }
                          }}
                          placeholder="Selecione mês/ano de recebimento (opcional)"
                          disabled={isFieldLocked}
                        />
                      </FormControl>
                      <FormDescription>
                        {paidMinMonthYear
                          ? `Não pode ser anterior a ${String(paidMinMonthYear.month).padStart(2, '0')}/${paidMinMonthYear.year}`
                          : 'Opcional'}
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <ReceivableDocumentsSection
                receivableId={isEdit ? id : undefined}
                pendingDocuments={isEdit ? [] : pendingDocuments}
                onPendingDocumentCreate={isEdit ? undefined : handlePendingDocumentCreate}
                onPendingDocumentUpdate={isEdit ? undefined : handlePendingDocumentUpdate}
                onPendingDocumentDelete={isEdit ? undefined : handlePendingDocumentDelete}
                isProcessing={uploadingPendingDocs}
                isLocked={false}
              />
              <ReceivableTaxesSection
                receivableId={isEdit ? id : undefined}
                pendingTaxes={isEdit ? [] : pendingTaxes}
                onPendingTaxCreate={isEdit ? undefined : handlePendingTaxCreate}
                onPendingTaxUpdate={isEdit ? undefined : handlePendingTaxUpdate}
                onPendingTaxDelete={isEdit ? undefined : handlePendingTaxDelete}
                isProcessing={uploadingPendingTaxes}
                receivableStatus={isEdit ? currentStatus : undefined}
              />

              <FormFooter
                isLoading={isSubmitting || updating}
              >
                <div className="flex gap-3 justify-between w-full">
                  <Button type="button" variant="outline" onClick={() => navigate('/receivables')} disabled={isSubmitting || updating}>
                    Cancelar
                  </Button>
                  <div className="flex gap-3">
                    {canMarkAsConcluded && (
                      <Button
                        type="button"
                        onClick={handleMarkAsConcluded}
                        disabled={isSubmitting || updating || !canMarkAsConcluded}
                        className="bg-green-600 hover:bg-green-700 text-white"
                      >
                        {isSubmitting || updating ? 'Processando...' : 'Salvar e Marcar como Concluído'}
                      </Button>
                    )}
                    <Button type="submit" disabled={isSubmitting || updating}>
                      {isSubmitting ? 'Salvando...' : isEdit ? 'Salvar alterações' : 'Criar Recebível'}
                    </Button>
                  </div>
                </div>
              </FormFooter>

            </form>
          </Form>
        </CardContent>
      </Card>

      {/* Diálogo de confirmação para marcar como concluído */}
      <AlertDialog open={showConfirmConcludedDialog} onOpenChange={setShowConfirmConcludedDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Marcar recebível como concluído?</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>
                Esta ação <strong>não pode ser desfeita</strong>. Ao confirmar:
              </p>
              <ul className="list-disc list-inside space-y-1 text-sm">
                <li>O recebível será marcado como concluído</li>
                <li>O sistema iniciará automaticamente o cálculo dos valores</li>
                <li>O processamento pode levar alguns minutos</li>
              </ul>
              <p className="pt-2 font-medium text-foreground">
                Deseja continuar?
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={updating}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmMarkAsConcluded}
              disabled={updating}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              {updating ? 'Processando...' : 'Sim, marcar como concluído'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

    </div >
  );
}
